import { useEffect } from "react";

const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_BASE_URL || "https://immobiliersolaire.fr";

/**
 * Lightweight head-meta manager (no react-helmet dep).
 * Injects <title>, <meta name="description">, og:*, twitter:*,
 * canonical URL, and JSON-LD Organization + LocalBusiness for SEO.
 */
export function Helmet({ title, description, ogImage, path = "/", schema, noindex = false }) {
  useEffect(() => {
    const set = (selector, attr, value, create) => {
      let el = document.head.querySelector(selector);
      if (!el && create) { el = document.createElement(create.tag); Object.entries(create.attrs).forEach(([k, v]) => el.setAttribute(k, v)); document.head.appendChild(el); }
      if (el && value != null) el.setAttribute(attr, value);
    };

    if (title) {
      document.title = title;
      // Mark that a page-specific title has been set, so ThemeContext won't overwrite it later
      window.__pageTitleOverride = true;
    }

    set('meta[name="description"]', "content", description || "", { tag: "meta", attrs: { name: "description" } });
    set('meta[property="og:title"]', "content", title || "", { tag: "meta", attrs: { property: "og:title" } });
    set('meta[property="og:description"]', "content", description || "", { tag: "meta", attrs: { property: "og:description" } });
    set('meta[property="og:type"]', "content", "website", { tag: "meta", attrs: { property: "og:type" } });
    set('meta[property="og:url"]', "content", `${PUBLIC_BASE}${path}`, { tag: "meta", attrs: { property: "og:url" } });
    set('meta[name="twitter:card"]', "content", "summary_large_image", { tag: "meta", attrs: { name: "twitter:card" } });
    if (ogImage) {
      set('meta[property="og:image"]', "content", ogImage, { tag: "meta", attrs: { property: "og:image" } });
      set('meta[name="twitter:image"]', "content", ogImage, { tag: "meta", attrs: { name: "twitter:image" } });
    }

    // Robots meta — explicit override (so admin pages stay private, public pages stay indexable)
    const robotsValue = noindex
      ? "noindex, nofollow, noarchive"
      : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
    set('meta[name="robots"]', "content", robotsValue, { tag: "meta", attrs: { name: "robots" } });

    // Canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", `${PUBLIC_BASE}${path}`);

    // JSON-LD schema (Organization with logo for Google Knowledge Panel + LocalBusiness + custom per-page)
    const orgNode = {
      "@type": "Organization",
      "@id": `${PUBLIC_BASE}#org`,
      name: "Immobilier Solaire",
      url: PUBLIC_BASE,
      logo: {
        "@type": "ImageObject",
        url: `${PUBLIC_BASE}/android-chrome-512x512.png`,
        width: 512,
        height: 512,
      },
      image: ogImage || `${PUBLIC_BASE}/og-image.png`,
    };
    const defaultLocalBusiness = {
      "@type": "LocalBusiness",
      "@id": `${PUBLIC_BASE}#localbusiness`,
      name: "Immobilier Solaire",
      image: ogImage || `${PUBLIC_BASE}/og-image.png`,
      logo: `${PUBLIC_BASE}/android-chrome-512x512.png`,
      telephone: "+33-2-38-74-50-60",
      priceRange: "€€",
      address: {
        "@type": "PostalAddress",
        streetAddress: "98 Route Nationale 20",
        addressLocality: "Cercottes",
        postalCode: "45520",
        addressRegion: "Centre-Val de Loire",
        addressCountry: "FR",
      },
      areaServed: ["Loiret", "Orléans", "Centre-Val de Loire", "Cercottes"],
      url: `${PUBLIC_BASE}${path}`,
    };

    // Merge custom schema with default Organization (Organization is always present sitewide).
    // If schema is given as { "@graph": [...] }, we merge its graph items with the default Organization.
    // If no schema given, we use Organization + default LocalBusiness.
    let finalSchema;
    if (schema && typeof schema === "object") {
      const customGraph = Array.isArray(schema["@graph"]) ? schema["@graph"] : [schema];
      finalSchema = {
        "@context": "https://schema.org",
        "@graph": [orgNode, ...customGraph],
      };
    } else {
      finalSchema = {
        "@context": "https://schema.org",
        "@graph": [orgNode, defaultLocalBusiness],
      };
    }
    const ldId = "ld-json-page";
    let ld = document.getElementById(ldId);
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = ldId; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(finalSchema);

    // Cleanup on unmount: remove the JSON-LD so it doesn't bleed into other pages
    return () => { const n = document.getElementById(ldId); if (n) n.remove(); };
  }, [title, description, ogImage, path, schema, noindex]);

  return null;
}
